import { type ComponentProps, useCallback, useState } from 'react';
import styled from 'styled-components';

import { isSortable } from '@dnd-kit/dom/sortable';
import { DragDropProvider } from '@dnd-kit/react';

import Foldout from 'components/shared/Foldout';
import SectionHeader from 'components/shared/form/SectionHeader';
import { colors } from 'styles/theme/colors';

import CategoryCard, { InheritedCategoryCard } from './CategoryCard';
import CriteriaSlideout from './CriteriaSlideout';
import type { CriteriaCategory } from './NewCategoryModal';
import NewCategoryModal from './NewCategoryModal';

export type CriteriaItem = {
  id?: string;
  _tempId?: string;
  criterion: string;
  description: string;
  category: string;
  /** Persisted id of the criterion's category. Absent when the category is new/unsaved (resolved by name instead). */
  categoryId?: string;
  order: number;
};

type DragEndHandler = NonNullable<ComponentProps<typeof DragDropProvider>['onDragEnd']>;

type Props = {
  categories: CriteriaCategory[];
  criteria: CriteriaItem[];
  onCategoriesChange: (categories: CriteriaCategory[]) => void;
  onCriteriaChange: (criteria: CriteriaItem[]) => void;
  /** Categories inherited (read-only) from the client-level AI criteria set. */
  inheritedCategories?: CriteriaCategory[];
  /** Criteria inherited (read-only) from the client-level AI criteria set. */
  inheritedCriteria?: CriteriaItem[];
  /** IDs of inherited criteria the group has chosen to hide. */
  hiddenAiCriteriaIds?: string[];
  onHiddenChange?: (ids: string[]) => void;
  /** Names to check for duplicates against when adding a new category (own + inherited). Defaults to own category names. */
  existingCategoryNames?: string[];
  /** True when editing a client-level set, so deleting a category cascades to every group that inherits it. */
  cascadesToGroups?: boolean;
};

export function generateTempCategoryId() {
  return `_temp_cat_${crypto.randomUUID()}`;
}

export function generateTempCriteriaId() {
  return `_temp_crit_${crypto.randomUUID()}`;
}

export default function CategoriesList({
  categories,
  criteria,
  onCategoriesChange,
  onCriteriaChange,
  inheritedCategories = [],
  inheritedCriteria = [],
  hiddenAiCriteriaIds = [],
  onHiddenChange,
  existingCategoryNames,
  cascadesToGroups = false,
}: Props) {
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [selectedCategoryId, setSelectedCategoryId] = useState<string | null>(null);
  const [selectedIsInherited, setSelectedIsInherited] = useState(false);
  const [slideoutOpen, setSlideoutOpen] = useState(false);

  // Categories that only exist on the inherited (client-level) set -- not owned by this group.
  // Categories with a matching name are treated as "shared": the group's own card/slideout is used,
  // and inherited criteria for that category are shown read-only alongside the group's own criteria.
  const ownCategoryNames = new Set(categories.map((c) => c.name.toLowerCase()));
  const readOnlyInheritedCategories = inheritedCategories.filter(
    (cat) => !ownCategoryNames.has(cat.name.toLowerCase())
  );

  const selectedCategory = selectedCategoryId
    ? ((selectedIsInherited ? readOnlyInheritedCategories : categories).find((c) => c._tempId === selectedCategoryId) ??
      null)
    : null;

  const handleDragEnd = useCallback(
    (...[event]: Parameters<DragEndHandler>) => {
      if (event.canceled) return;
      const { source } = event.operation;

      if (!source || !isSortable(source)) return;

      const oldIndex = source.initialIndex;
      const newIndex = source.index;

      if (oldIndex === newIndex) return;

      const reordered = [...categories];
      const [moved] = reordered.splice(oldIndex, 1);

      reordered.splice(newIndex, 0, moved);

      onCategoriesChange(reordered.map((cat, i) => ({ ...cat, order: i })));
    },
    [categories, onCategoriesChange]
  );

  const handleAddCategory = (category: Pick<CriteriaCategory, 'name' | 'color'>) => {
    const newCategory: CriteriaCategory = {
      _tempId: generateTempCategoryId(),
      ...category,
      order: categories.length,
    };

    onCategoriesChange([...categories, newCategory]);
    setShowCategoryModal(false);
  };

  const handleEditCategory = (updates: Pick<CriteriaCategory, 'name' | 'color'>) => {
    if (!selectedCategory) return;

    const updatedCategories = categories.map((c) =>
      c._tempId === selectedCategory._tempId ? { ...c, name: updates.name, color: updates.color } : c
    );

    onCategoriesChange(updatedCategories);
  };

  const handleDeleteCategory = (cat: CriteriaCategory) => {
    const updatedCategories = categories.filter((c) => c._tempId !== cat._tempId).map((c, i) => ({ ...c, order: i }));
    const updatedCriteria = criteria.filter((c) => (cat.id ? c.categoryId !== cat.id : c.category !== cat.name));

    onCategoriesChange(updatedCategories);
    onCriteriaChange(updatedCriteria);
    setSlideoutOpen(false);
    setSelectedCategoryId(null);
  };

  const handleCategoryClick = (cat: CriteriaCategory, isInherited = false) => {
    setSelectedCategoryId(cat._tempId);
    setSelectedIsInherited(isInherited);
    setSlideoutOpen(true);
  };

  const handleApplyCriteria = (updatedCriteria: CriteriaItem[]) => {
    if (!selectedCategory) return;

    // Replace criteria for this category, keeping other categories intact
    const otherCriteria = criteria.filter((c) =>
      selectedCategory.id ? c.categoryId !== selectedCategory.id : c.category !== selectedCategory.name
    );

    onCriteriaChange([...otherCriteria, ...updatedCriteria]);
  };

  // Own (group) criteria are keyed by categoryId once the category is persisted (rename-safe). A
  // brand-new, unsaved category has no id yet, so its criteria are keyed by name client-side until
  // save, when the backend resolves the category by name (see criteriaSetPayload.ts).
  const getCriteriaForCategory = (category: CriteriaCategory) =>
    criteria.filter((c) => (category.id ? c.categoryId === category.id : c.category === category.name));

  const getCriteriaCount = (category: CriteriaCategory) => getCriteriaForCategory(category).length;

  // Inherited (client) criteria are always keyed by the client category's id. When `category` here IS
  // the client category (the read-only inherited card), its `id` already resolves directly. When
  // `category` is the group's own category that merely shares a name with a client category ("shared"
  // display), the two are different rows with different ids, so resolve the client category's id by
  // name first, then filter by id. Falls back to name matching only if no matching category row exists.
  const getInheritedCriteriaForCategory = (category: CriteriaCategory) => {
    const inheritedCategoryId = inheritedCategories.find(
      (ic) => ic.name.toLowerCase() === category.name.toLowerCase()
    )?.id;

    return inheritedCategoryId
      ? inheritedCriteria.filter((c) => c.categoryId === inheritedCategoryId)
      : inheritedCriteria.filter((c) => c.category.toLowerCase() === category.name.toLowerCase());
  };

  const getInheritedCriteriaCount = (category: CriteriaCategory) => getInheritedCriteriaForCategory(category).length;

  return (
    <>
      <HeaderRow>
        <SectionHeader>Criteria Categories</SectionHeader>
        <AddLink onClick={() => setShowCategoryModal(true)}>+ Add New Category</AddLink>
      </HeaderRow>

      {/* Inherited (client) categories render first, matching the inherited-first evaluated order. */}
      {readOnlyInheritedCategories.length > 0 && (
        <CardList>
          {readOnlyInheritedCategories.map((cat) => (
            <InheritedCategoryCard
              key={cat._tempId}
              category={cat}
              criteriaCount={getInheritedCriteriaCount(cat) + getCriteriaCount(cat)}
              onClick={() => handleCategoryClick(cat, true)}
            />
          ))}
        </CardList>
      )}

      {categories.length > 0 && (
        <DragDropProvider onDragEnd={handleDragEnd}>
          <CardList>
            {categories.map((cat, index) => {
              const count = getCriteriaCount(cat) + getInheritedCriteriaCount(cat);

              return (
                <CategoryCard
                  key={cat._tempId}
                  category={cat}
                  index={index}
                  criteriaCount={count}
                  onClick={() => handleCategoryClick(cat)}
                />
              );
            })}
          </CardList>
        </DragDropProvider>
      )}

      <NewCategoryModal
        show={showCategoryModal}
        onCancel={() => setShowCategoryModal(false)}
        onSubmit={handleAddCategory}
        existingNames={existingCategoryNames ?? categories.map((c) => c.name)}
      />

      <Foldout open={slideoutOpen} setOpen={setSlideoutOpen} closeOnBackdropClick width='min(85%, 917px)'>
        {selectedCategory && (
          <CriteriaSlideout
            key={selectedCategory._tempId}
            handleClose={() => setSlideoutOpen(false)}
            category={selectedCategory}
            criteria={getCriteriaForCategory(selectedCategory)}
            inheritedCriteria={getInheritedCriteriaForCategory(selectedCategory)}
            hiddenAiCriteriaIds={hiddenAiCriteriaIds}
            onApplyHidden={onHiddenChange}
            allCategories={categories}
            onEditCategory={handleEditCategory}
            onDeleteCategory={() => handleDeleteCategory(selectedCategory)}
            onApply={handleApplyCriteria}
            isInherited={selectedIsInherited}
            cascadesToGroups={cascadesToGroups}
          />
        )}
      </Foldout>
    </>
  );
}

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  > *:first-child {
    margin-bottom: 0;
  }
`;

const AddLink = styled.span`
  color: ${colors.blue100};
  font-size: 14px;
  cursor: pointer;
  font-weight: 500;

  &:hover {
    text-decoration: underline;
  }
`;

const CardList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 24px;
`;
